import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import withDescriptionData from 'core/hocs/withDescriptionData';
import { Conference as ConferenceType, ConferencePreview } from 'types/models/Conference';
import {
  conferenceRequestConfig,
  GetConferenceResult,
  GetConferenceVariables,
} from 'graphql/queries/GetConference.query';
import {
  conferencesContactsRequestConfig,
  GetConferencesContactsResults,
} from 'graphql/queries/GetConferencesContacts.query';
import { GetPageLayoutResult, serverRequests as pageLayoutServerRequests } from '@layouts/PageLayout/PageLayout.utils';
import ConferencePage from '@pages/Conference';
import graphQLRequest from 'utils/graphQLRequest';
import combineRequests, { RequestConfig } from 'utils/combineRequest';
import handleError from 'utils/handleError';
import { startTransaction } from 'utils/startTransaction';
import omit from 'utils/omit';

const { routes } = config;

const serverRequests: RequestConfig[] = [
  ...pageLayoutServerRequests,
  conferenceRequestConfig,
  conferencesContactsRequestConfig,
];

type Result = GetPageLayoutResult &
  GetConferencesContactsResults & {
    conference: ConferenceType;
    futureConferences: ConferencePreview[];
  };

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(
  withDescriptionData(
    async ({ res, params, query }, { client }) => {
      const name = params?.name as string;
      const isPreview = query?.preview === 'true';

      if (!name) {
        return handleError(res, 404);
      }

      const transaction = startTransaction({ op: 'server.requests', name: routes.conferences.name.href });
      const conferenceDataSpan = transaction.startChild({ op: 'combined-data' });
      const { data: combinedData } = await graphQLRequest<
        GetPageLayoutResult & GetConferenceResult & GetConferencesContactsResults,
        GetConferenceVariables
      >(client, combineRequests(serverRequests), { variables: { name } });
      conferenceDataSpan.finish();

      if (!combinedData) {
        transaction.finish();
        return handleError(res, 500);
      }

      const [conference] = combinedData.conferences;

      if (!conference) {
        transaction.finish();
        return handleError(res, 404);
      }

      if (conference.isHidden && !isPreview) {
        transaction.finish();
        return handleError(res, 503);
      }

      transaction.finish();

      return {
        props: {
          ...omit(combinedData, ['conferences']),
          conference,
          breadcrumbs: [{ text: conference.seoData?.header }],
        },
      };
    },
    {
      descriptionsGetter: ({ conference }) => [conference.seoData?.description],
    }
  )
);

const Conference = ({
  conference,
  futureConferences,
  employees,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <ConferencePage conference={conference} futureConferences={futureConferences} employees={employees} />
);

export default withPageLayout(Conference, {
  showBreadcrumb: true,
  breadcrumbVariant: 'conferences',
  breadcrumbLinkVariant: 'real',
});
